enum LinkErrorType {
  CALLBACK_UNDEFINED = 'callback_undefined',
  DDS_NOT_SUPPORTED = 'dds_not_supported',
  EXPIRED_USER_TOKEN = 'expired_user_token',
  GENERIC = 'generic',
  INCOMPATIBLE_DDS_CONFIG = 'incompatible_dds_config',
  INVALID_ACCOUNT_ID = 'invalid_account_id',
  INVALID_DDS_CONFIG = 'invalid_dds_config',
  INVALID_ITEMS = 'invalid_items',
  INVALID_USER_TOKEN = 'invalid_user_token',
}

export enum UIEventName {
  INTRO_OPENED = 'intro - opened',
  SEARCH_OPENED = 'search - opened',
  SEARCH_TERM_UPDATED = 'search - term updated',
  SEARCH_LINK_ITEM_SELECTED = 'search - link item selected',
  LOGIN_OPENED = 'login - opened',
  LOGIN_FORM_SUBMITTED = 'login - form submitted',
  LOGIN_LOGIN_HELP_CLICKED = 'login - login help clicked',
  MFA_OPENED = 'mfa - opened',
  SUCCESS_OPENED = 'success - opened',
  ACCOUNT_STATUS_OPENED = 'account status - opened',
  ACCOUNT_STATUS_DISCONNECTED = 'account status - disconnected',
  DDS_SETTINGS_OPENED = 'dds settings - opened',
  ALLOCATION_TYPE_SELECTION_OPENED = 'allocation type selection - opened',
  DDS_SUCCESS_OPENED = 'dds success - opened',
  ACCOUNT_ERROR_OPENED = 'account error - opened',
  ERROR_OPENED = 'error - opened',
  LINK_CLOSED = 'link closed',
}

enum LoginErrorCode {
  AUTH_REQURIRED = 'auth_required',
  EXPIRED_CREDENTIALS = 'expired_credentials',
  INVALID_AUTH = 'invalid_auth',
  INVALID_CREDENTIALS = 'invalid_credentials',
  INVALID_LOGIN_METHOD = 'invalid_login_method',
  MFA_TIMEOUT = 'mfa_timeout',
}

enum MFAType {
  SMS_CODE = 'sms_code',
  EMAIL_CODE = 'email_code',
  SECRET_QUESTION = 'secret_question',
  EMAIL_LINK_CONFIRMATION = 'email_link_confirmation',
  OTP_CODE = 'otp_code',
  EMAIL_MESSAGE_CONFIRMATION = 'email_message_confirmation',
}

enum ConnectionStatus {
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  AVAILABILITY = 'availability',
  ERROR = 'error',
}

enum ConnectionErrorCode {
  ACCOUNT_DISABLED = 'account_disabled',
  ACCOUNT_INACCESSIBLE = 'account_inaccessible',
  ACCOUNT_INCOMPLETE = 'account_incomplete',
  ACCOUNT_NOT_FOUND = 'account_not_found',
  AUTH_REQUIRED = 'auth_required',
  CONNECTION_UNAVAILABLE = 'connection_unavailable',
  CREDENTIALS_MANAGED_BY_ORGANIZATION = 'credentials_managed_by_organization',
  EXPIRED_CREDENTIALS = 'expired_credentials',
  FULL_AUTH_REQUIRED = 'full_auth_required',
  INVALID_ACCOUNT_TYPE = 'invalid_account_type',
  INVALID_AUTH = 'invalid_auth',
  INVALID_CREDENTIALS = 'invalid_credentials',
  INVALID_LOGIN_METHOD = 'invalid_login_method',
  INVALID_MFA = 'invalid_mfa',
  LOGIN_ATTEMPTS_EXCEEDED = 'login_attempts_exceeded',
  MFA_ATTEMPTS_EXCEEDED = 'mfa_attempts_exceeded',
  MFA_EXHAUSTED = 'mfa_exhausted',
  MFA_NOT_CONFIGURED = 'mfa_not_configured',
  MFA_TIMEOUT = 'mfa_timeout',
  ONGOING_REFRESH_DISABLED = 'ongoing_refresh_disabled',
  PHYSICAL_MFA_UNSUPPORTED = 'physical_mfa_unsupported',
  SERVICE_UNAVAILABLE = 'service_unavailable',
  SYSTEM_ERROR = 'system_error',
  TOS_REQUIRED = 'tos_required',
  TRIAL_CONNECTIONS_EXHAUSTED = 'trial_connections_exhausted',
  TRIAL_PERIOD_EXPIRED = 'trial_period_expired',
  UNSUPPORTED_AUTH_TYPE = 'unsupported_auth_type',
  UNSUPPORTED_BUSINESS_ACCOUNT = 'unsupported_business_account',
  UNSUPPORTED_LANGUAGE = 'unsupported_language',
  UNSUPPORTED_MFA_METHOD = 'unsupported_mfa_method',
  USER_ACTION_TIMEOUT = 'user_action_timeout',
}

export enum InternalArgyleEvents {
  LOGIN_FAILED = 'login - failed',
  LOGIN_SUCCEEDED = 'login - succeeded',
  ERROR_OPENED = 'error - opened',
}

export type CloseMethod = 'footer_button' | 'x' | null

export type AccountData = {
  accountId: string
  userId: string
  itemId: string
}

export type FormData = {
  accountId: string
  userId: string
}

export type LinkErrorData = {
  userId: string
  errorType: LinkErrorType
  errorMessage?: string
  errorDetails?: string
}

type UIEventProperties = {
  deepLink: boolean
  userId: string
  accountId?: string
  itemId?: string
}

type IntroOpenedEvent = {
  name: UIEventName.INTRO_OPENED
  properties: null
}

type SearchProperties = {
  term: string
  tab: 'Popular' | 'Payroll providers' | 'Employers'
} & UIEventProperties

type SearchOpenedEvent = {
  name: UIEventName.SEARCH_OPENED
  properties: SearchProperties
}

type SearchTermUpdatedEvent = {
  name: UIEventName.SEARCH_TERM_UPDATED
  properties: SearchProperties
}

type SearchLinkItemSelectedEvent = {
  name: UIEventName.SEARCH_LINK_ITEM_SELECTED
  properties: SearchProperties
}

type LoginProperties = {
  errorCode: LoginErrorCode
  errorMessage: string
} & UIEventProperties

type LoginOpenedEvent = {
  name: UIEventName.LOGIN_OPENED
  properties: LoginProperties
}

type LoginFormSubmittedEvent = {
  name: UIEventName.LOGIN_FORM_SUBMITTED
  properties: null
}

type LoginLoginHelpClickedEvent = {
  name: UIEventName.LOGIN_LOGIN_HELP_CLICKED
  properties: null
}

type MFAProperties = {
  type: 'Login' | 'DDS'
  mfaType: MFAType
} & UIEventProperties

type MFAOpenedEvent = {
  name: UIEventName.MFA_OPENED
  properties: MFAProperties
}

type SuccessOpenedEvent = {
  name: UIEventName.SUCCESS_OPENED
  properties: null
}

type AccountStatusOpenedEvent = {
  name: UIEventName.ACCOUNT_STATUS_OPENED
  properties: UIEventProperties
}

type AccountStatusDisconnectedEvent = {
  name: UIEventName.ACCOUNT_STATUS_DISCONNECTED
  properties: null
}

type DDSSettingsOpenedEvent = {
  name: UIEventName.DDS_SETTINGS_OPENED
  properties: {
    allocationType: 'percent' | 'amount' | 'none'
  }
}

type AllocationTypeSelectionOpenedEvent = {
  name: UIEventName.ALLOCATION_TYPE_SELECTION_OPENED
  properties: null
}

type DDSSuccessOpenedEvent = {
  name: UIEventName.DDS_SUCCESS_OPENED
  properties: null
}

type AccountProperties = {
  connectionStatus: ConnectionStatus
  connectionErrorCode: ConnectionErrorCode
} & UIEventProperties

type AccountErrorOpenedEvent = {
  name: UIEventName.ACCOUNT_ERROR_OPENED
  properties: AccountProperties
}

type ErrorOpenedEvent = {
  name: UIEventName.ERROR_OPENED
  properties: {
    errorType: LinkErrorType
  }
}

type LinkClosedEvent = {
  name: UIEventName.LINK_CLOSED
  properties: {
    closeMethod: CloseMethod
  }
}

export type UIEvent =
  | IntroOpenedEvent
  | SearchOpenedEvent
  | SearchTermUpdatedEvent
  | SearchLinkItemSelectedEvent
  | LoginOpenedEvent
  | LoginFormSubmittedEvent
  | LoginLoginHelpClickedEvent
  | MFAOpenedEvent
  | SuccessOpenedEvent
  | AccountStatusOpenedEvent
  | AccountStatusDisconnectedEvent
  | DDSSettingsOpenedEvent
  | AllocationTypeSelectionOpenedEvent
  | DDSSuccessOpenedEvent
  | AccountErrorOpenedEvent
  | ErrorOpenedEvent
  | LinkClosedEvent

type LinkConfig = {
  userToken: string
  sandbox: boolean
  flowId?: string
  ddsConfig?: string
  items?: string[]
  accountId?: string
  // Account callbacks
  onAccountConnected?: (data: AccountData) => void
  onAccountCreated?: (data: AccountData) => void
  onAccountError?: (data: AccountData) => void
  onAccountRemoved?: (data: AccountData) => void
  // Depost switching callbacks
  onDDSSuccess?: (data: AccountData) => void
  onDDSError?: (data: AccountData) => void
  // User flow callbacks
  onCantFindItemClicked: () => void
  onClose?: () => void
  onDocumentsSubmitted?: (data: FormData) => void
  onFormSubmitted?: (data: FormData) => void
  onUIEvent?: (data: UIEvent) => void
  // Operational callbacks
  onError?: (data: LinkErrorData) => void
  onTokenExpired?: (updateToken: (newToken: string) => void) => void
}

export type Argyle = {
  create: (options: LinkConfig) => {
    open: () => void
    close: () => void
  }
}

export type ArgyleLinkInstance = {
  open: () => void
  close: () => void
}
